import React, { useEffect, useRef, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useStore } from '@store/StoreProvider'
import { toastStore } from '@store/ToastStore'
import { AuthPageShell } from '@app/auth/AuthPageShell'
import { verifyEmailByToken } from '@/api/emailVerificationApi'
import { completeEmailVerification } from '@utils/apiHelpers'
import { postAuthLandingPath } from '@constants/adminNav'
import { t } from '@store/I18nStore'

type VerifyStatus = 'pending' | 'success' | 'error'

export default function VerifyEmailPage() {
  const { i18n, auth } = useStore()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [status, setStatus] = useState<VerifyStatus>('pending')
  const [errorText, setErrorText] = useState(t('errors.auth.confirm_link_invalid'))
  const hasVerified = useRef(false)

  useEffect(() => {
    if (hasVerified.current) return
    hasVerified.current = true

    const token = (searchParams.get('token') || '').trim()
    if (!token) {
      setErrorText(t('errors.auth.no_confirm_token'))
      setStatus('error')
      return
    }

    const base = (i18n.apiBaseUrl || '').replace(/\/$/, '')
    if (!base) {
      setErrorText(t('errors.api_url_missing_reload'))
      setStatus('error')
      return
    }

    void (async () => {
      try {
        const result = await verifyEmailByToken(base, token)
        if (result.requiresLogin) auth.logout({ skipRemote: true })
        const signedIn = !result.requiresLogin && auth.isAuthenticated
        if (signedIn) await completeEmailVerification()
        const cleanUrl = new URL(window.location.href)
        cleanUrl.searchParams.delete('token')
        window.history.replaceState({}, '', `${cleanUrl.pathname}${cleanUrl.search}${cleanUrl.hash}`)
        toastStore.show(result.message || t('auth.email_confirm.success'), 'success')
        setStatus('success')
        window.setTimeout(() => {
          if (signedIn) {
            navigate(postAuthLandingPath(auth.currentUser?.role), { replace: true })
            return
          }
          navigate('/login', { replace: true })
        }, 1200)
      } catch (err) {
        const text = err instanceof Error ? err.message : t('errors.auth.check_email')
        setErrorText(text)
        setStatus('error')
        toastStore.show(text, 'error')
      }
    })()
  }, [auth.currentUser?.role, auth.isAuthenticated, i18n.apiBaseUrl, navigate, searchParams])

  if (status === 'pending') {
    return (
      <AuthPageShell title={t('auth.confirm_email.page_title')}>
        <p className="text-center text-slate-300">{t('auth.loading.check_link')}</p>
      </AuthPageShell>
    )
  }

  if (status === 'error') {
    return (
      <AuthPageShell title={t('errors.auth.confirm_failed')}>
        <p className="text-center text-slate-300">{errorText}</p>
        <div className="mt-5 text-center text-sm text-slate-400">
          <Link to="/login" className="text-brand2 font-semibold hover:underline">{t('auth.go_to_login_page')}</Link>
        </div>
      </AuthPageShell>
    )
  }

  return (
    <AuthPageShell title={t('auth.email_confirm.confirmed')}>
      <p className="text-center text-slate-300">
        {auth.isAuthenticated ? t('auth.loading.opening_dashboard') : t('auth.loading.redirect_to_login')}
      </p>
    </AuthPageShell>
  )
}
